import React from 'react';
import ReactDOM from 'react-dom/client';
import { Provider } from 'react-redux';
import { PersistGate } from 'redux-persist/integration/react';
import { persistStore } from 'redux-persist';
import store from './core/store';
import App from './App';
import { scheduleRemovePreloader } from './core/utils';
import { resolveGoogleFlow } from './core/utils/googleFlow';
import type { GtgEventPayload } from './core/utils/gtgDataLayer';
import { selectGoogleFlow, setGoogleFlow } from './core/store/plans';
import { parseOnboardingPath } from './locales/languageConfig';

declare global {
  interface Window {
    ga: any;
    fbq: any;
    uetq?: { push(...args: unknown[]): unknown };
    AF: any;
    gtag: (
      command: 'get',
      target: string,
      fieldName: 'client_id',
      callback: (value?: string) => void,
    ) => void;
    google_tag_manager: any;
    google_tag_gateway: any;
    dataLayer: Array<GtgEventPayload | Record<string, unknown> | IArguments>;
    AF_SMART_SCRIPT: any;
    ApplePaySession: any;
    TiktokJelly: any;
    hj: any;
    ttq: any;
    oaiq: any;
    google: {
      accounts: {
        id: {
          initialize: (config: {
            client_id: string;
            callback: (response: { credential: string }) => void;
            ux_mode: string;
            auto_select: boolean;
            cancel_on_tap_outside: boolean;
          }) => void;
          prompt: () => void;
          renderButton: (
            element: HTMLElement,
            options: {
              type: 'standard';
              theme: 'outline';
              size: 'large';
              text: 'continue_with';
              shape: 'rectangular';
              logo_alignment: 'left';
              width: string;
            },
          ) => void;
        };
      };
      maps: any;
    };
  }
}

const root = ReactDOM.createRoot(document.getElementById('root') as HTMLElement);

const persistor = persistStore(store);

const prepareApp = () => {
  const persistedFlow = selectGoogleFlow(store.getState());
  const googleFlow = resolveGoogleFlow(
    parseOnboardingPath(window.location.pathname).cleanPath,
    persistedFlow,
  );

  store.dispatch(setGoogleFlow(googleFlow));
  scheduleRemovePreloader();
};

root.render(
  <Provider store={store}>
    <PersistGate loading={null} persistor={persistor} onBeforeLift={prepareApp}>
      <App />
    </PersistGate>
  </Provider>,
);
